/*
 * Component: Product form gates
 * Purpose: Let more than one script hold up the same add-to-cart submit —
 * today kl-engrave.js (an unfinished engraving must not reach the cart) and
 * kl-gift-wrap.js (an add-on line to attach first) — without any of them
 * fighting over the same event, and without a third script that only needs
 * to know "is anything still pending" (kl-cart-instant-open.js, deciding
 * whether to open the drawer early) having to know every gate's name.
 *
 * A single "add to cart" click can pass through more than one gate before it
 * reaches product-form.js for real. Each gate intercepts the submit, does
 * its own work (open a dialog and wait for confirmation, or an async fetch),
 * then replays the submit so the next gate — or the real handler — gets a
 * turn. A gate must not re-intercept a submit it has already cleared, or the
 * chain loops forever; it must also not swallow a submit a DIFFERENT gate is
 * clearing, or that gate's replay never reaches product-form.js.
 *
 * So the mark is per gate, not shared: window.klFormGateState keeps one
 * { registered, cleared } pair of Sets per form. A gate registers its name
 * as soon as it binds to a form — before it has necessarily seen a submit —
 * so hasPendingGate can tell "no gate exists here" apart from "a gate exists
 * but has not cleared yet" without a caller needing to know every gate's
 * name in advance. Each gate then marks its own name cleared once it lets a
 * submit through, and every name clears back out once product-form.js's own
 * submit listener has had its turn (see clearGateStateOnRealSubmit) — the
 * one moment every gate agrees a full cycle has actually finished.
 *
 * Loaded once, before every script that calls into it — see the comment
 * beside its <script> tag in layout/theme.liquid for why order matters here.
 */
window.klFormGateState = window.klFormGateState || new WeakMap();

function getGateState(form) {
  if (!window.klFormGateState.has(form)) {
    window.klFormGateState.set(form, { registered: new Set(), cleared: new Set() });
  }
  return window.klFormGateState.get(form);
}

function registerGate(form, gateName) {
  getGateState(form).registered.add(gateName);
}

/*
 * For a gate whose registration is conditional on its own state, not just
 * its presence — kl-gift-wrap.js only intercepts while its checkbox is
 * checked, so it must stop counting as pending the moment it is unchecked.
 */
function unregisterGate(form, gateName) {
  getGateState(form).registered.delete(gateName);
}

function hasGateCleared(form, gateName) {
  return getGateState(form).cleared.has(gateName);
}

function markGateCleared(form, gateName) {
  getGateState(form).cleared.add(gateName);
}

function hasPendingGate(form) {
  const { registered, cleared } = getGateState(form);

  return [...registered].some((gateName) => !cleared.has(gateName));
}

/*
 * product-form.js binds its own submit listener directly on the form
 * (bubble phase, no capture), so it always runs after every capture-phase
 * gate above has let the event through — the reset added here runs
 * alongside it. Every gate script calls this for its own form; the dataset
 * guard makes a second call from a different gate a no-op, so the reset
 * does not depend on which gates happen to be present on a given product
 * (a page with gift-wrap but no engrave block must still reset).
 */
function clearGateStateOnRealSubmit(form) {
  if (form.dataset.klFormGateResetBound === 'true') return;

  form.dataset.klFormGateResetBound = 'true';
  form.addEventListener('submit', () => {
    getGateState(form).cleared.clear();
  });
}
